import React, { useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Pagination,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import { createStyles, withStyles } from '@mui/styles';
import PropTypes from 'prop-types';

import BorderColorIcon from '@mui/icons-material/BorderColor';
import DeleteIcon from '@mui/icons-material/Delete';

import { DateConverter } from '../../utils/DateConverter';
import EditConnectorForm from '../connectors/EditConnectorForm';
import { deleteSecret } from "../../api's/ConnectorsApi";
import LoadingIcon from '../../reusable-component/LoadingIcon';

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    background: 'none !important',
    color: '#8798AD',
    textTransform: 'uppercase',
    fontSize: 12,
    fontWeight: 600,
    padding: "10px"
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "15px"
  }
}));

const StyledTableRow = withStyles(() =>
  createStyles({
    root: {
      background: '#FFFFFF',
      boxShadow: '0px 2px 6px #0000000A',
      borderRadius: '5px !important',
    },
  })
)(TableRow);

export default function SecretTable({
  tableData,
  search,
  setSearch,
  page,
  totalPages,
  onPageChange,
}) {
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSecretId, setSelectedSecretId] = useState(null);

  const [isOpen, setIsOpen] = useState(false);
  const [connectorType, setConnectorType] = useState('');
  const [connectorId, setConnectorId] = useState('');
  const [secretName, setSecretName] = useState('');

  const filteredData = useMemo(() => tableData, [tableData]);

  const handleDeleteClick = id => {
    setSelectedSecretId(id);
    setDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedSecretId) return;

    try {
      setLoading(true);
      await deleteSecret(selectedSecretId);
      // refresh handled by parent
    } catch (err) {
      console.error("Delete error:", err);
    } finally {
      setLoading(false);
      setDialogOpen(false);
      setSelectedSecretId(null);
    }
  };

  return (
    <Box>

      {/* SEARCH — only updates text, no API here */}
      <TextField
        label="Search"
        size="small"
        value={search}
        onChange={e => setSearch(e.target.value)}
        autoComplete="off"
        sx={{ mb: 2 }}
      />

      <TableContainer>
        <Table style={{ minWidth: 650, borderSpacing: '0px 4px', borderCollapse: 'separate' }}>
          <TableHead>
            <TableRow>
              <StyledTableCell>Connection Name</StyledTableCell>
              <StyledTableCell>Connector Type</StyledTableCell>
              <StyledTableCell>Created By</StyledTableCell>
              <StyledTableCell>Created On</StyledTableCell>
              <StyledTableCell>Action</StyledTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredData?.map(row => (
              <StyledTableRow key={row.id}>
                <StyledTableCell>{row.secret_name}</StyledTableCell>
                <StyledTableCell>{row.connector_type}</StyledTableCell>
                <StyledTableCell>{row.username}</StyledTableCell>
                <StyledTableCell>{DateConverter(row.createdAt)}</StyledTableCell>

                <StyledTableCell>
                  <IconButton
                    onClick={() => {
                      setConnectorType(row.connector_type);
                      setConnectorId(row.id);
                      setSecretName(row.secret_name);
                      setIsOpen(true);
                    }}
                  >
                    <BorderColorIcon />
                  </IconButton>

                  <IconButton onClick={() => handleDeleteClick(row.id)}>
                    <DeleteIcon />
                  </IconButton>
                </StyledTableCell>
              </StyledTableRow>
            ))}

            {!filteredData?.length && (
              <StyledTableRow>
                <StyledTableCell colSpan={5} align="center">No data</StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* PAGINATION */}
      {totalPages > 1 && (
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, p) => onPageChange(e, p)}
          sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
          size="small"
        />
      )}

      {isOpen && (
        <EditConnectorForm
          open={isOpen}
          handleClose={() => setIsOpen(false)}
          connector={connectorType}
          id={connectorId}
          secretName={secretName}
        />
      )}

      {loading && <LoadingIcon />}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>Are you sure you want to delete this?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

SecretTable.propTypes = {
  tableData: PropTypes.array,
  search: PropTypes.string,
  setSearch: PropTypes.func,
  page: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func,
};
